import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Check, Eye, EyeOff, Minus } from 'lucide-react';

import { cn } from '../../lib/cn';
import { readValue, useReadOnly } from '../../lib/read-only';

/** The shared control shell, so every field is the same height and radius. */
const CONTROL = [
  'w-full rounded-control border bg-surface px-3 text-sm text-body',
  'placeholder:text-faint',
  'transition-colors',
  'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-faint',
  // A red ring rather than red text: the text is the user's own value and
  // recolouring it makes their input look like the error message.
  'aria-invalid:border-danger aria-invalid:ring-1 aria-invalid:ring-danger/30',
  'border-line focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30',
].join(' ');

/**
 * How tall a control is, on the same scale as `Button`'s `size`.
 *
 * `md` is a form field. `sm` is a control that sits in a row of other small
 * ones — a list row's switch, type and actions beside `size="sm"` buttons — so
 * the row lines up without anybody hand-tuning a height.
 */
export type ControlSize = 'sm' | 'md';

const CONTROL_SIZE: Record<ControlSize, string> = {
  sm: 'h-8 text-xs',
  md: 'h-9.5',
};

export interface FieldShellProps {
  label?: string;
  /** Marks the control required, and says so where a user can see it. */
  required?: boolean;
  /** Guidance shown before anything goes wrong. */
  hint?: string;
  /** The problem. Replaces `hint` so the two never stack and compete. */
  error?: string;
  className?: string;
  children?: ReactNode;
}

/**
 * Label, control, and the one message beneath it.
 *
 * The error replaces the hint rather than appearing beside it: the moment
 * something is wrong, the only thing worth reading is what to do about it.
 */
export function Field({
  label,
  required,
  hint,
  error,
  className,
  children,
  htmlFor,
}: FieldShellProps & { htmlFor?: string }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label ? (
        <label htmlFor={htmlFor} className="block text-xs font-medium text-muted">
          {label}
          {required ? (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {children}
      {error ? (
        // `role="alert"` so a screen reader announces the failure rather than
        // leaving it to be discovered by tabbing back.
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-faint">{hint}</p>
      ) : null}
    </div>
  );
}

// `prefix` is omitted from the HTML attributes as well as `size`: the DOM one
// is a string (the RDFa attribute), and ours is a node.
export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: string;
  hint?: string;
  error?: string;
  /** Rendered inside the control, before the value — a search glyph, a currency. */
  prefix?: ReactNode;
  suffix?: ReactNode;
  containerClassName?: string;
  /** `sm` beside small buttons and selects in a row; see {@link ControlSize}. */
  size?: ControlSize;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, prefix, suffix, className, containerClassName, id, required, size = 'md', ...rest },
  ref,
) {
  const generated = useId();
  const inputId = id ?? generated;
  if (useReadOnly()) {
    // Read, not edited: the value as text, its prefix (a currency, a glyph)
    // beside it — never a control the reader cannot use (lib/read-only.tsx).
    return (
      <Field label={label} hint={hint} className={containerClassName}>
        <p className="flex min-h-8 items-center gap-1.5 text-sm text-body">
          {prefix ? <span className="text-faint">{prefix}</span> : null}
          {rest.type === 'password' ? '••••••••' : readValue(rest.value ?? rest.defaultValue)}
        </p>
      </Field>
    );
  }

  const control = (
    <input
      ref={ref}
      id={inputId}
      required={required}
      // Points assistive technology at the message, which is what turns a red
      // border into something a non-sighted user can act on.
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${inputId}-message` : undefined}
      className={cn(CONTROL, CONTROL_SIZE[size], prefix && 'pl-9', suffix && 'pr-9', className)}
      {...rest}
    />
  );

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={inputId}
      className={containerClassName}
    >
      {prefix || suffix ? (
        <div className="relative">
          {prefix ? (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint">
              {prefix}
            </span>
          ) : null}
          {control}
          {suffix ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint">{suffix}</span>
          ) : null}
        </div>
      ) : (
        control
      )}
    </Field>
  );
});

export type PasswordInputProps = Omit<InputProps, 'type' | 'suffix'>;

/**
 * A password field with a show / hide toggle.
 *
 * The toggle is a real button, so it is reachable by keyboard and named for a
 * screen reader, and `type="button"` so pressing it never submits the form. It
 * changes only how the value is shown: the value, the autocomplete hint and
 * what the browser offers to save are untouched.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(props, ref) {
    const [visible, setVisible] = useState(false);
    const Icon = visible ? EyeOff : Eye;
    return (
      <Input
        ref={ref}
        {...props}
        type={visible ? 'text' : 'password'}
        suffix={
          <button
            type="button"
            onClick={() => setVisible((shown) => !shown)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-pressed={visible}
            className="flex cursor-pointer items-center rounded-sm text-faint hover:text-body focus-visible:text-accent focus-visible:outline-none"
          >
            <Icon className="size-3.5" aria-hidden />
          </button>
        }
      />
    );
  },
);

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Applied to the field wrapper, so a form grid can span it. */
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, containerClassName, id, required, rows = 3, ...rest },
  ref,
) {
  const generated = useId();
  const textareaId = id ?? generated;
  if (useReadOnly()) {
    return (
      <Field label={label} hint={hint} className={containerClassName}>
        <p className="text-sm whitespace-pre-wrap text-body">{readValue(rest.value ?? rest.defaultValue)}</p>
      </Field>
    );
  }
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={textareaId}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        // A textarea scrolls itself — the one place SimpleBar cannot wrap — so its
        // own bar is the slim themed one (.scroll-thin, tokens.css).
        className={cn(CONTROL, 'scroll-thin resize-y py-2 leading-relaxed', className)}
        {...rest}
      />
    </Field>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'size'> {
  label?: string;
  hint?: string;
  error?: string;
  options: readonly SelectOption[];
  /** Applied to the field wrapper, so a form grid can span it. */
  containerClassName?: string;
  /** `sm` beside small buttons in a row; see {@link ControlSize}. */
  size?: ControlSize;
  /**
   * The text for the empty choice.
   *
   * Given explicitly rather than defaulted to a blank, because an unlabelled
   * empty option reads as a value — "(none)" and "Choose a client" mean
   * different things and only the field knows which.
   */
  placeholder?: string;
}

/**
 * A native `select`.
 *
 * Native on purpose. It gets keyboard behaviour, mobile pickers and
 * accessibility right for free, and the token system already styles it through
 * `color-scheme`. A custom listbox is worth building only where an option needs
 * rich content — which is what the searchable combobox in the table filters is
 * for.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    options,
    placeholder,
    className,
    containerClassName,
    id,
    required,
    size = 'md',
    ...rest
  },
  ref,
) {
  const generated = useId();
  const selectId = id ?? generated;
  if (useReadOnly()) {
    const chosen = String(rest.value ?? rest.defaultValue ?? '');
    return (
      <Field label={label} hint={hint} className={containerClassName}>
        <p className="flex min-h-8 items-center text-sm text-body">
          {options.find((option) => option.value === chosen)?.label ?? readValue(chosen)}
        </p>
      </Field>
    );
  }
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={selectId}
      className={containerClassName}
    >
      <select
        ref={ref}
        id={selectId}
        required={required}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL, CONTROL_SIZE[size], 'cursor-pointer pr-8', className)}
        {...rest}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  hint?: string;
  /** Neither checked nor unchecked — a header box over a partial selection. */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, indeterminate, className, id, ...rest },
  ref,
) {
  const generated = useId();
  const boxId = id ?? generated;
  if (useReadOnly()) {
    return <ReadChoice on={Boolean(rest.checked ?? rest.defaultChecked)} label={label} hint={hint} className={className} />;
  }
  return (
    <label
      htmlFor={boxId}
      className={cn('flex cursor-pointer items-start gap-2.5 text-sm', className)}
    >
      <input
        // `indeterminate` is a DOM property with no attribute, so it can only be
        // set imperatively. The ref callback does it on every render, which is
        // what keeps a header box in step with the selection beneath it.
        ref={(node) => {
          if (node) node.indeterminate = indeterminate ?? false;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        id={boxId}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded border-line-strong text-accent accent-accent focus:ring-1 focus:ring-accent/30"
        {...rest}
      />
      {label || hint ? (
        <span className="space-y-0.5">
          {label ? <span className="block text-body">{label}</span> : null}
          {hint ? <span className="block text-xs text-faint">{hint}</span> : null}
        </span>
      ) : null}
    </label>
  );
});

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  /** `sm` beside small buttons in a row: a smaller track and an `xs` label. */
  size?: ControlSize;
}

/**
 * A toggle.
 *
 * A real checkbox under a styled track, rather than a `div` with a click
 * handler: the input carries the role, the keyboard behaviour and the form
 * value, and the appearance is only appearance.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, hint, className, id, disabled, size = 'md', ...rest },
  ref,
) {
  const generated = useId();
  const switchId = id ?? generated;
  const small = size === 'sm';
  if (useReadOnly()) {
    return (
      <ReadChoice
        on={Boolean(rest.checked ?? rest.defaultChecked)}
        label={label}
        hint={hint}
        className={cn(small && 'text-xs', className)}
      />
    );
  }
  return (
    <label
      htmlFor={switchId}
      className={cn(
        small ? 'flex items-center gap-2' : 'flex items-start gap-3',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <span className={cn('relative inline-flex shrink-0', !small && 'mt-0.5')}>
        <input
          ref={ref}
          id={switchId}
          type="checkbox"
          role="switch"
          disabled={disabled}
          className="peer size-0 opacity-0"
          {...rest}
        />
        <span
          aria-hidden
          className={cn(
            'block rounded-pill bg-line-strong transition-colors peer-checked:bg-accent peer-focus-visible:ring-2 peer-focus-visible:ring-accent/40',
            small ? 'h-4 w-7' : 'h-5 w-9',
          )}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute left-0.5 top-0.5 rounded-full bg-white shadow-card transition-transform',
            small ? 'size-3 peer-checked:translate-x-3' : 'size-4 peer-checked:translate-x-4',
          )}
        />
      </span>
      {label || hint ? (
        <span className={cn('space-y-0.5', small ? 'text-xs' : 'text-sm')}>
          {label ? <span className="block font-medium text-body">{label}</span> : null}
          {hint ? <span className="block text-xs text-muted">{hint}</span> : null}
        </span>
      ) : null}
    </label>
  );
});

/** A checkbox's or a switch's state as it reads — on or off, beside its label. */
function ReadChoice({
  on,
  label,
  hint,
  className,
}: {
  on: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  className?: string;
}) {
  const Icon = on ? Check : Minus;
  return (
    <div className={cn('flex items-start gap-2.5 text-sm', className)}>
      <Icon className={cn('mt-0.5 size-4 shrink-0', on ? 'text-success' : 'text-faint')} aria-hidden />
      <span className="space-y-0.5">
        <span className="block text-body">
          <span className="sr-only">{on ? 'On: ' : 'Off: '}</span>
          {label ?? (on ? 'On' : 'Off')}
        </span>
        {hint ? <span className="block text-xs text-faint">{hint}</span> : null}
      </span>
    </div>
  );
}
